import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { buildSelectOptions } from '@/components/Form/FormField'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { POIDocumentType } from 'api/Clients/Types/DocumentType'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { NameDto } from 'api/Common/Types'
import {
  DocumentCategoryType,
  DocumentStatusId,
  isDocumentSanitized,
  isDocumentStatusDeleted,
  isDocumentStatusRejected,
  isNotifyClientAllowedByCategoryType,
  TagType,
  UpdateClientDocument,
} from 'api/Documents/Types/ClientDocument'
import { DocumentDto } from 'api/Documents/Types/Document'
import { DocumentRequestType } from 'api/Documents/Types/DocumentRequests'
import { DocumentHistory } from 'api/Documents/Types/DocumentStatusHistory'
import { DocumentStatusReasonDto } from 'api/Documents/Types/DocumentStatusReasonDto'
import { UpdateDocumentStatusReasonDto } from 'api/Documents/Types/UpdateDocumentStatusReasonDto'
import { isDepartmentsPayments } from 'api/Identity/Types/DepartmentType'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { Box, FlexBox } from 'components/Box'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridState } from 'features/DataGrid'
import { MoreIcon } from 'icons/MoreIcon'
import { ChevronDown } from 'lucide-react'
import { RoleTagId } from 'routes/Administration/Users/RoleTag'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientDocumentTypeAction } from 'store/clients/clients.actions'
import {
  fetchDocumentCategoriesTypeAction,
  fetchDocumentStatusesAction,
  fetchDocumentStatusReasonsActions,
  fetchDocumentTagTypeAction,
  getDocumentsTypesByCategory,
} from 'store/documents/documents.actions'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { DocumentApiClient } from 'utils/api/documentApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { isFileTypeImage } from 'utils/check-file-type'
import { dateComparator, formatLocalDateUTC } from 'utils/date'
import { otherDocumentRequestTypeList } from 'utils/documents/documentRequestTypes'
import { isEmpty, isNotEmpty } from 'utils/is-empty'
import { showMediaWindow } from 'utils/media-window'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { generateClassNameForCell } from 'utils/table'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import HistoryDetailsModal from '../HistoryDetailsModal'
import { AddDocumentModal } from './AddDocumentModal'
import { MassRejectionReasonModal } from './AddDocumentModal/MassRejectionReasonModal'
import { BulkCategoryUpdateModal } from './BulkCategortyUpdateModal'
import { BulkUpdateFormValues } from './BulkCategoryUpdateForm'
import { ClientDetailsDocumentDetailed } from './ClientDetailsDocumentDetailed'
import { ClientDetailsRequestsSearchMenuItems } from './ClientDetailsRequestsSearchMenuItems'
import ClientDocumentReports from './ClientDocumentReports'
import { ExpandDetailCell } from './DocumentExpandCell'
import { DocumentTranslationFormUI } from './DocumentTranslationForm'
import { EditDocumentForm } from './EditDocumentForm'

import styles from './ClientDetailsDocument.module.scss'

interface Props {
  data: ClientDetailedDto

  forceRefresh(): void

  setDocumentPreviewModal({ id, name }: { id: string; name: string }): void
  updateClientStatus(values: UpdateClientStatus): void
}

const useMapStateToProps = () => {
  const [auth] = useContext(AuthSessionContext)

  return {
    auth: auth,
    dateFormatType: auth?.user.dateFormatType.name,
    categoryType: useAppSelector((state) => state.documents.categoryType),
    documentType: useAppSelector((state) => state.clients.documentType),
    documentTypesByCategory: useAppSelector((state) => state.documents.documentTypesByCategory),
    status: useAppSelector((state) => state.documents.statuses),
    tag: useAppSelector((state) => state.documents.tag),
    statusReasons: useAppSelector((state) => state.documents.statusReasons),
    countries: useAppSelector((state) => state.identity.countries),
  }
}

export const CustomCategories: Record<string, NameDto> = {
  All: { id: 0, name: 'All' },
  Personal: { id: 1, name: 'Personal' },
  Statement: { id: 12, name: 'Statement' },
}

export const DocumentCategories: NameDto[] = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Personal' },
  { id: 12, name: 'Statement' },
]

const ClientDetailsDocuments: React.FC<Props> = (props) => {
  const { data, forceRefresh, setDocumentPreviewModal, updateClientStatus } = props

  const setToast = useContext(ToastContext)

  const documentApiClient = useApiClient(DocumentApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [documents, setDocuments] = useState<DocumentDto[]>([])
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null)
  const [documentRequestModal, setDocumentRequestModal] = useState<{
    visible: boolean
    request?: string
    function?: () => void
  }>({
    visible: false,
  })
  const [reasonModalData, setReasonModalData] = useState<UpdateDocumentStatusReasonDto | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState<null | string>(null)
  const [editDocumentModal, setEditDocumentModal] = useState<null | DocumentDto>(null)
  const [addDocumentModal, setAddDocumentModal] = useState(false)
  const [translationModal, setTranslationModal] = useState<{
    visible: boolean
    data: DocumentDto | null
  }>({
    visible: false,
    data: null,
  })
  const [historyDetailsModal, setHistoryDetailsModal] = useState<null | { id: string }>(null)
  const [reportModal, setReportModal] = useState<null | {
    document: DocumentDto
    reportId?: string
  }>(null)

  const [selectedCategory, setSelectedCategory] = useState<number>(CustomCategories.All.id)

  const mapStateToProps = useMapStateToProps()
  const { auth, status, statusReasons, documentType, documentTypesByCategory } = mapStateToProps
  const { dateFormatType, tag, categoryType, countries } = mapStateToProps
  const permissions = auth?.permissions || []
  const departmentIds = (auth?.user?.departments || []).map((x) => x.id)

  const { gridState, gridActions } = useGridState()
  const gridFilterState = createGridState(getPaginationInitialState(), {})

  const searchFilter = useMemo(() => {
    let filter = `Client == ${data.id}`
    if (selectedCategory === CustomCategories.Personal.id) {
      filter = `(${filter}) AND (Category != ${CustomCategories.Statement.id})`
    } else if (selectedCategory === CustomCategories.Statement.id) {
      filter = `(${filter}) AND (Category == ${CustomCategories.Statement.id})`
    }
    return filter
  }, [data.id, selectedCategory])

  const { callback: fetchData, forceRefresh: forceRefreshDocuments } =
    useCallbackWithForceRefresh(() => {
      return documentApiClient.getDocumentsByClient(
        searchFilter,
        gridFilterState.pageIndex,
        gridFilterState.pageSize
      )
    }, [searchFilter])

  const { data: clientDocuments, isLoading } = useFetchOne(fetchData)

  const [reasonInput, setReasonInput] = useState<string | null>(null)
  const [tempReason, setTempReason] = useState<{
    id: string
    statusReason: DocumentStatusReasonDto
  } | null>(null)
  const [massRejectionReasonModal, setMassRejectionReasonModal] = useState(false)
  const [bulkDocumentUpdateModal, setBulkDocumentUpdateModal] = useState(false)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isEmpty(categoryType)) dispatch(fetchDocumentCategoriesTypeAction(documentApiClient))
    if (isEmpty(tag)) dispatch(fetchDocumentTagTypeAction(documentApiClient))
    if (isEmpty(status)) dispatch(fetchDocumentStatusesAction(documentApiClient))
    if (isEmpty(documentType)) dispatch(fetchClientDocumentTypeAction(clientsApiClient))
    if (isEmpty(statusReasons)) dispatch(fetchDocumentStatusReasonsActions(documentApiClient))
    if (clientDocuments) {
      setSortedDocuments(clientDocuments.items)
      gridActions.setGridFilters({
        ...gridFilterState,
        total: clientDocuments.totalResults,
      })
    }
  }, [clientDocuments]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (deleteModalId) {
      await documentApiClient.deleteDocument(deleteModalId)
      setDeleteModalId(null)
      setToast(successToast('Document deleted.'))
      forceRefreshDocuments()
    }
  }

  const handleUpdateDocumentReasonStatus = async () => {
    if (reasonModalData) {
      await documentApiClient.updateDocumentStatusReasons(reasonModalData.id, reasonModalData)
      setReasonModalData(null)
      setToast(successToast('Document status reason updated.'))
      forceRefreshDocuments()
    }
  }

  const handleVerify = async (id: string) => {
    try {
      setSubmitting(true)
      await documentApiClient.verifyDocument(id)
      setToast(successToast('Document sent for verification.'))
      forceRefreshDocuments()
    } finally {
      setSubmitting(false)
    }
  }

  const showAddDocumentModal = (): void => {
    setAddDocumentModal(true)
  }

  const showTranslationModal = (documentDto: DocumentDto): void => {
    setTranslationModal({ visible: true, data: documentDto })
  }

  const showEditDocumentModal = (document: DocumentDto) => {
    setEditDocumentModal(document)
  }

  const showDocumentPreviewModal = async (document: {
    id: string
    name: string
  }): Promise<void> => {
    const documentFile = await documentApiClient.getDocumentUrl(document.id)

    if (documentFile?.url && documentFile?.filename && isFileTypeImage(documentFile.filename)) {
      showMediaWindow(documentFile.url, documentFile.filename)
    } else {
      setDocumentPreviewModal({ id: document.id, name: document.name })
    }
  }

  const showReportModal = (document: DocumentDto): void => {
    setReportModal({ document })
  }

  const showHistoryDetails = (id: string): void => {
    setHistoryDetailsModal({ id })
  }

  const setSortedDocuments = (docs: DocumentDto[]) => {
    setDocuments(
      [...docs]
        .map((doc) => ({ ...doc, expanded: false }))
        .sort((a, b) => dateComparator(a.createdAt, b.createdAt, true))
    )
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const docs = (documents || []).map((item) => {
      if (item.id === event.dataItem.id && !isDocumentSanitized(item.id)) {
        return {
          ...item,
          selected: !event.dataItem.selected,
        }
      }
      return item
    })
    setSortedDocuments(docs)
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    const docs = (documents || []).map((item) => {
      if (isDocumentSanitized(item.id)) {
        return item
      }
      return { ...item, selected: isChecked }
    })
    setSortedDocuments(docs)
  }

  const getSelected = () => {
    return documents.filter((doc) => doc.selected).map((x) => x.id)
  }

  const verifySelectedDocuments = async () => {
    const ids = getSelected()
    if (ids) {
      try {
        setSubmitting(true)
        await documentApiClient.verifyDocuments(ids)
        setToast(successToast('Document sent for verification.'))
        forceRefresh()
        setSortedDocuments(documents.map((doc) => ({ ...doc, selected: false })))
      } finally {
        setSubmitting(false)
      }
    }
  }

  const prepareStatusPerDocumentCategoryAndReasonData = (
    categoryId: number,
    documentStatusReasonDtos: DocumentStatusReasonDto[] = []
  ): { [key: string]: DocumentStatusReasonDto[] } => {
    return documentStatusReasonDtos
      .filter((s) => s.category.id === categoryId)
      .filter((s) => {
        const documentReason = documentStatusReasonDtos.find((x) => x.id === s.id)
        const statusId = documentReason?.status?.id
        return !isDocumentStatusDeleted(statusId)
      })
      .reduce<{ [key: string]: DocumentStatusReasonDto[] }>((acc, item) => {
        if (!acc[item.status.name]) {
          acc[item.status.name] = []
        }
        acc[item.status.name].push(item)
        return acc
      }, {})
  }

  const setReasonData = ({
    id,
    statusReason,
  }: {
    id: string
    statusReason: DocumentStatusReasonDto
  }) => {
    if (statusReason.name === 'Other') {
      setTempReason({ id, statusReason })
      return setReasonInput(' ')
    }
    return setReasonModalData({
      id,
      reasonId: statusReason.id,
      reason: statusReason.isDefault
        ? statusReason.status.name
        : statusReason.status.name + ` (${statusReason.name})`,
    })
  }

  const sendClientDocumentStatusNotification = async (documentId: string) => {
    await clientAreaApiClient.sentDocumentStatusNotification(data.id, documentId)
    setDocumentRequestModal({ visible: false })
    setToast(successToast('Notification to client sent.'))
    forceRefreshDocuments()
  }

  const sendClientDocumentPasswordRequest = async (documentId: string) => {
    await clientAreaApiClient.sendDocumentPasswordRequest(data.id, documentId)
    setDocumentRequestModal({ visible: false })
    setToast(successToast('Password request send.'))
    forceRefresh()
  }

  const renderMenu = (dataItem: DocumentDto) => {
    const statusPerDocumentCategoryAndReasonData = prepareStatusPerDocumentCategoryAndReasonData(
      dataItem.category.id,
      statusReasons
    )
    const isPaymentsDocument = dataItem.category.id === DocumentCategoryType.Payments
    const statusItems = Object.keys(statusPerDocumentCategoryAndReasonData).map((stat) => {
      const reasons = statusPerDocumentCategoryAndReasonData[stat]

      if (reasons.length > 1) {
        return (
          <DropdownMenuSub key={stat}>
            <DropdownMenuSubTrigger>{stat}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {reasons.map((documentStatusReason) => (
                <DropdownMenuItem
                  key={documentStatusReason.id}
                  onSelect={() =>
                    setReasonData({
                      id: dataItem.id,
                      statusReason: documentStatusReason,
                    })
                  }
                >
                  {documentStatusReason.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )
      }

      return (
        <DropdownMenuItem
          key={reasons[0].id}
          onSelect={() =>
            setReasonData({
              id: dataItem.id,
              statusReason: reasons[0],
            })
          }
        >
          {reasons[0].status.name}
        </DropdownMenuItem>
      )
    })

    return (
      <>
        <DropdownMenuItem
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanEditClientDocuments) ||
            isDocumentStatusDeleted(dataItem.status.id) ||
            isDocumentSanitized(dataItem.id) ||
            (isPaymentsDocument &&
              !hasPermission(permissions, PermissionsEnum.CanEditPaymentsDocument))
          }
          onSelect={() => showEditDocumentModal(dataItem)}
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={
            !hasPermission(permissions, PermissionsEnum.CanDeleteClientDocuments) ||
            isDocumentStatusDeleted(dataItem.status.id) ||
            isDocumentSanitized(dataItem.id) ||
            (isPaymentsDocument &&
              !hasPermission(permissions, PermissionsEnum.CanDeletePaymentsDocument))
          }
          onSelect={() => setDeleteModalId(dataItem.id)}
          variant='destructive'
        >
          Delete
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={
            isDocumentSanitized(dataItem.id) ||
            !hasPermission(permissions, PermissionsEnum.CanViewDocumentAuditLog)
          }
          onSelect={() => setAuditLogVisible(dataItem.id)}
        >
          Audit Log
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanVerifyDocuments) ||
              isDocumentSanitized(dataItem.id) ||
              isDocumentStatusDeleted(dataItem.status.id)
            }
          >
            Verification
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onSelect={() => handleVerify(dataItem.id)}>Verify</DropdownMenuItem>
            <DropdownMenuItem
              disabled={
                !hasPermission(
                  permissions,
                  PermissionsEnum.CanViewClientIdentityVerificationResults
                ) ||
                isDocumentSanitized(dataItem.id) ||
                isDocumentStatusDeleted(dataItem.status.id)
              }
              onSelect={() => showReportModal(dataItem)}
            >
              Reports
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEditClientDocuments) ||
              isDocumentSanitized(dataItem.id) ||
              isDocumentStatusDeleted(dataItem.status.id)
            }
          >
            Status
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {statusItems}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={
                !hasPermission(permissions, PermissionsEnum.CanEmailDocumentStatus) ||
                isDocumentStatusDeleted(dataItem.status.id) ||
                isDocumentSanitized(dataItem.id) ||
                !isNotifyClientAllowedByCategoryType(dataItem.category.id)
              }
              onSelect={() =>
                setDocumentRequestModal({
                  visible: true,
                  function: () => sendClientDocumentStatusNotification(dataItem.id),
                  request: 'Notify Client',
                })
              }
            >
              Notify Client
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEmailDocumentPasswordRequest) ||
              isDocumentSanitized(dataItem.id) ||
              isDocumentStatusDeleted(dataItem.status.id)
            }
          >
            Requests
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              disabled={
                !hasPermission(permissions, PermissionsEnum.CanEmailDocumentPasswordRequest)
              }
              onSelect={() => {
                setDocumentRequestModal({
                  visible: true,
                  function: () => sendClientDocumentPasswordRequest(dataItem.id),
                  request: 'Password Request',
                })
              }}
            >
              Password Request
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {isNotEmpty(dataItem.translations) && <DropdownMenuSeparator />}
        {isNotEmpty(dataItem.translations) && (
          <DropdownMenuItem onSelect={() => showTranslationModal(dataItem)}>
            Translation
          </DropdownMenuItem>
        )}
      </>
    )
  }

  const renderRequestMenu = () => (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          disabled={!hasPermission(permissions, PermissionsEnum.CanRequestEmailClientDocuments)}
        >
          Requests
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <ClientDetailsRequestsSearchMenuItems
            items={[
              {
                id: POIDocumentType.AdditionalNationalId,
                name: 'Request National ID',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AdditionalNationalId),
                    request: 'Request National ID',
                  }),
              },
              {
                id: POIDocumentType.AdditionalPassport,
                name: 'Request Passport',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AdditionalPassport),
                    request: 'Request Passport',
                  }),
              },
              {
                id: POIDocumentType.AdditionalPOIGeneric,
                name: 'Request POI (any)',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AdditionalPOIGeneric),
                    request: 'Request POI (any)',
                  }),
              },
              {
                id: POIDocumentType.AdditionalPOI,
                name: 'Additional POI',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AdditionalPOI),
                    request: 'Additional POI',
                  }),
              },
              {
                id: POIDocumentType.AdditionalResidencePermit,
                name: 'Request Residence Permit',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AdditionalResidencePermit),
                    request: 'Request Residence Permit',
                  }),
              },
              {
                id: POIDocumentType.AddressClarification,
                name: 'Address Clarification',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.AddressClarification),
                    request: 'Address Clarification',
                  }),
              },
              {
                id: POIDocumentType.BacksidePOI,
                name: 'POI Backside',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.BacksidePOI),
                    request: 'POI Backside',
                  }),
              },
              ...[
                {
                  id: POIDocumentType.BankStatement,
                  name: 'Request Bank Statement',
                  function: () =>
                    setDocumentRequestModal({
                      visible: true,
                      function: () => sendClientRequest(POIDocumentType.BankStatement),
                      request: 'Request Bank Statement',
                    }),
                },
              ].filter(() => isDepartmentsPayments(departmentIds)),
              {
                id: POIDocumentType.DrivingLicense,
                name: 'Request Driving License',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.DrivingLicense),
                    request: 'Request Driving License',
                  }),
              },
              {
                id: POIDocumentType.FrontsidePOI,
                name: 'POI Frontside',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.FrontsidePOI),
                    request: 'POI Frontside',
                  }),
              },
              {
                id: POIDocumentType.POA,
                name: 'Request POA',
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientRequest(POIDocumentType.POA),
                    request: 'Request POA',
                  }),
              },
              ...[
                {
                  id: POIDocumentType.ProofOfDeposit,
                  name: 'Request Proof of Deposit',
                  function: () =>
                    setDocumentRequestModal({
                      visible: true,
                      function: () => sendClientRequest(POIDocumentType.ProofOfDeposit),
                      request: 'Request Proof of Deposit',
                    }),
                },
              ].filter(() => isDepartmentsPayments(departmentIds)),
              ...otherDocumentRequestTypeList.poi.map((x) => ({
                id: x.value,
                name: x.label,
                function: () =>
                  setDocumentRequestModal({
                    visible: true,
                    function: () => sendClientOtherRequestEmail({ requestType: x.value }),
                    request: x.label,
                  }),
              })),
            ]}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger>Actions</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuItem onSelect={handleApproveAll}>Approve All</DropdownMenuItem>
          <DropdownMenuItem onSelect={handleClickMassRejection}>Reject All</DropdownMenuItem>
          <DropdownMenuItem onSelect={handleExpireAll}>Expire All</DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuItem
        onSelect={() => setBulkDocumentUpdateModal(true)}
        disabled={!allowBulkUpdate}
      >
        Bulk Edit
      </DropdownMenuItem>
    </>
  )

  const handleApproveAll = async () => {
    await documentApiClient.updateDocumentStatusMassApprove(data.id, {
      id: data.id,
      documentIds: documents.filter((x) => x.selected).map((x) => x.id),
      statusId: DocumentStatusId.APPROVED,
    })
    forceRefresh()
  }

  const handleExpireAll = async () => {
    const ids = documents.filter((x) => x.selected).map((x) => x.id)
    await documentApiClient.updateDocumentStatusMassExpire(data.id, ids)
    forceRefresh()
  }

  const handleRejectAll = async (
    addressDocumentRejectionReason?: DocumentStatusReasonDto,
    personalDocumentRejectionReason?: DocumentStatusReasonDto
  ) => {
    await documentApiClient.updateDocumentStatusMassReject(data.id, {
      id: data.id,
      documentIds: documents
        .filter((x) => x.selected && x.category.id !== DocumentCategoryType.Statements)
        .map((x) => x.id),
      statusId: DocumentStatusId.REJECTED,
      AddressRejectionReason: addressDocumentRejectionReason
        ? { Id: addressDocumentRejectionReason.id, Text: addressDocumentRejectionReason.name ?? '' }
        : undefined,
      PersonalRejectionReason: personalDocumentRejectionReason
        ? {
            Id: personalDocumentRejectionReason.id,
            Text: personalDocumentRejectionReason.name ?? '',
          }
        : undefined,
    })
    forceRefresh()
  }

  const sendClientRequest = async (requestDocumentType?: POIDocumentType) => {
    if (requestDocumentType) {
      await clientAreaApiClient.updateClientPOIRequest(data.id, requestDocumentType)
      setDocumentRequestModal({ visible: false })
      setToast(successToast('Email sent.'))
      forceRefresh()
    } else setDocumentRequestModal({ visible: false })
  }

  const sendClientOtherRequestEmail = async ({
    requestType,
  }: {
    requestType: DocumentRequestType
  }) => {
    await clientAreaApiClient.sendOtherRequest({ clientId: data.id, requestType })
    setToast(successToast('Email sent.'))
    forceRefresh()
  }

  const headerSelectionValue = (): boolean => {
    if (documents.length <= 0) {
      return false
    }

    return (documents || []).findIndex((dataItem) => !dataItem.selected) === -1
  }

  const getDocumentTypeBasedOnCategory = (categoryId: string): void => {
    dispatch(getDocumentsTypesByCategory(categoryId, documentApiClient))
  }

  const onSubmit = async (values: UpdateClientDocument) => {
    await documentApiClient.updateDocumentClient(values)
    setEditDocumentModal(null)
    forceRefreshDocuments()
    setToast(successToast('Document updated successfully.'))
    forceRefresh()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    gridActions.setGridFilters({
      take,
      skip,
      pageIndex,
    })

    const response = await documentApiClient.getDocumentsByClient(searchFilter, pageIndex, pageSize)
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setDocuments(response.items)
    }
  }

  const handleExpand = (expandedDocument: DocumentDto) => {
    setDocuments(
      documents.map((doc) =>
        doc.id === expandedDocument.id
          ? {
              ...doc,
              expanded: expandedDocument.expanded,
            }
          : doc
      )
    )
  }

  const prepareStatusPerDocumentCategoryAndReason = (documentCategoryType: DocumentCategoryType) =>
    prepareStatusPerDocumentCategoryAndReasonData(documentCategoryType, statusReasons).Rejected

  const [isPersonalDocumentSelected, isAddressDocumentSelected] = useMemo(() => {
    const selected = (documentCategoryType: DocumentCategoryType) =>
      documents.some((doc) => doc.category.id === documentCategoryType && doc.selected)
    return [selected(DocumentCategoryType.Personal), selected(DocumentCategoryType.Address)]
  }, [documents])

  const personalDocumentRejectionReasons = useMemo(() => {
    return prepareStatusPerDocumentCategoryAndReason(DocumentCategoryType.Personal)
  }, [statusReasons]) // eslint-disable-line react-hooks/exhaustive-deps

  const addressDocumentRejectionReasons = useMemo(() => {
    return prepareStatusPerDocumentCategoryAndReason(DocumentCategoryType.Address)
  }, [statusReasons]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickMassRejection = () => {
    if (isPersonalDocumentSelected || isAddressDocumentSelected) {
      setMassRejectionReasonModal(true)
    } else {
      handleRejectAll()
    }
  }

  const handleBulkDocumentCategoryUpdate = async ({
    documentCategory,
    documentType: requestDocumentType,
  }: BulkUpdateFormValues) => {
    if (documentCategory && documentType && requestDocumentType) {
      await documentApiClient.massUpdateDocuments({
        documentIds: getSelected(),
        categoryId: documentCategory,
        typeId: requestDocumentType,
      })

      setBulkDocumentUpdateModal(false)
      forceRefreshDocuments()
      setToast(successToast('Documents updated successfully.'))
      forceRefresh()
    }
  }

  const allowBulkUpdate = useMemo(() => {
    return getSelected().length > 1
  }, [documents]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || submitting) {
    return <Spinner />
  }

  return (
    <Fragment>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.DOCUMENTS}
        updateClientStatus={updateClientStatus}
      />
      {bulkDocumentUpdateModal && (
        <BulkCategoryUpdateModal
          closeModal={() => setBulkDocumentUpdateModal(false)}
          onSubmitForm={handleBulkDocumentCategoryUpdate}
          clientDocumentCategoryTypes={categoryType}
          clientDocumentTypes={documentType}
          getDocumentTypeBasedOnCategory={getDocumentTypeBasedOnCategory}
        />
      )}
      {massRejectionReasonModal && (
        <MassRejectionReasonModal
          closeModal={() => setMassRejectionReasonModal(false)}
          personalDocumentRejectionReasons={personalDocumentRejectionReasons}
          addressDocumentRejectionReasons={addressDocumentRejectionReasons}
          onSubmitForm={handleRejectAll}
          isAddressDocumentSelected={isAddressDocumentSelected}
          isPersonalDocumentSelected={isPersonalDocumentSelected}
        />
      )}
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={!!auditLogVisible}
          onClose={() => setAuditLogVisible(null)}
          documentId={auditLogVisible}
        />
      )}
      {reportModal && (
        <Modal
          visible={!!reportModal}
          width={1100}
          padding='2.5rem'
          onClose={() => setReportModal(null)}
        >
          <ClientDocumentReports
            document={reportModal.document}
            client={data}
            reportId={reportModal.reportId}
          />
        </Modal>
      )}
      {!!historyDetailsModal && (
        <HistoryDetailsModal
          type='document'
          id={historyDetailsModal.id}
          hideModal={() => setHistoryDetailsModal(null)}
        />
      )}
      {editDocumentModal && (
        <EditDocumentForm
          isOpen={!!editDocumentModal}
          categoriesType={categoryType}
          document={editDocumentModal}
          documentStatus={status}
          tag={tag}
          client={data}
          dateFormatType={dateFormatType}
          countries={countries}
          documents={documents
            .filter((d) => d.id !== editDocumentModal.id)
            .map((x) => ({ id: x.id, name: x.originalFilename, tags: x.tags }))}
          documentType={documentType}
          documentTypesByCategory={documentTypesByCategory}
          getDocumentTypeBasedOnCategory={getDocumentTypeBasedOnCategory}
          onSubmit={onSubmit}
          cancel={() => setEditDocumentModal(null)}
        />
      )}
      {addDocumentModal && (
        <AddDocumentModal
          client={data}
          forceRefresh={forceRefreshDocuments}
          closeModal={() => setAddDocumentModal(false)}
        />
      )}
      {translationModal.visible && translationModal.data && (
        <Modal
          width={1100}
          visible={!!translationModal.visible}
          onClose={() => setTranslationModal({ visible: false, data: null })}
        >
          <DocumentTranslationFormUI translations={translationModal.data.translations} />
        </Modal>
      )}
      {reasonInput && tempReason && (
        <Modal width={400} visible={!!reasonInput} onClose={() => setReasonInput(null)}>
          <Box display='flex' flexDirection='column' style={{ width: '90%', margin: 32 }}>
            <div style={{ width: '100%', display: 'flex' }}>
              <label style={{ fontWeight: 'bold', width: '15%' }}>Reason</label>
              <input
                style={{
                  color: '#0e76eb',
                  border: 'none',
                  width: '60%',
                  borderBottom: '1px solid rgba(176, 180, 186, 0.42)',
                  transition: 'all 0.2s ease',
                  backgroundColor: 'transparent',
                  fontSize: 14,
                  outline: 'none',
                  textAlign: 'left',
                }}
                onChange={(e) => setReasonInput(e.target.value)}
                type='text'
                value={reasonInput}
              />
            </div>
            <FlexBox alignItems={'center'} justifyContent={'center'}>
              <Button
                style={{ marginTop: 24 }}
                onClick={() => {
                  setReasonModalData({
                    id: tempReason.id,
                    reason: reasonInput.trim(),
                    reasonId: tempReason.statusReason.id,
                  })
                  setReasonInput(null)
                }}
              >
                {'Save'}
              </Button>
            </FlexBox>
          </Box>
        </Modal>
      )}
      <ConfirmationModal
        contentText='Are you sure you wish to delete the document?'
        yesButtonText='Yes, delete'
        onYes={handleDelete}
        onCancel={() => setDeleteModalId(null)}
        visible={!!deleteModalId}
      />
      <ConfirmationModal
        contentText={`Are you sure you want to set the status of the document ${reasonModalData?.reason}.`}
        yesButtonText='Yes'
        onYes={handleUpdateDocumentReasonStatus}
        onCancel={() => setReasonModalData(null)}
        visible={!!reasonModalData}
      />
      {documentRequestModal.function && (
        <ConfirmationModal
          contentText={`Are you sure you wish to send the following request: ${documentRequestModal.request}?`}
          yesButtonText='Send Request'
          onYes={documentRequestModal.function}
          onCancel={() => setDocumentRequestModal({ visible: false })}
          visible={documentRequestModal.visible}
        />
      )}
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='space-between'>
        <Button
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientDocuments)}
          onClick={showAddDocumentModal}
        >
          Add documents
        </Button>
        <ButtonGroup>
          <Button
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanVerifyDocuments) ||
              getSelected().length > 2 ||
              getSelected().length < 1
            }
            onClick={verifySelectedDocuments}
          >
            Verify Documents
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button type='button' variant='outline' size='icon' aria-label='More options' />
              }
            >
              <ChevronDown />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>{renderRequestMenu()}</DropdownMenuContent>
          </DropdownMenu>
        </ButtonGroup>
      </FlexBox>

      <FlexBox style={{ marginBottom: 40 }} alignItems='center'>
        <label style={{ marginRight: 8 }}>Category</label>
        <Select
          className='max-w-50'
          value={selectedCategory}
          options={buildSelectOptions(DocumentCategories)}
          onChange={(c) => setSelectedCategory(c)}
        />
      </FlexBox>
      <Grid
        {...gridState}
        resizable
        selectedField='selected'
        expandField='expanded'
        data={documents}
        sort={gridState.sort}
        total={gridState.total}
        take={gridState.take}
        skip={gridState.skip}
        detail={(props) => (
          <ClientDetailsDocumentDetailed
            {...props}
            permissions={permissions}
            showDocumentPreviewModal={showDocumentPreviewModal}
          />
        )}
        onPageChange={handleGridPageChange}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        <Column
          field='expanded'
          width='50px'
          cell={(props) => <ExpandDetailCell {...props} expandChange={handleExpand} />}
          headerCell={() => <span />}
        />
        <Column field='selected' width='50px' headerSelectionValue={headerSelectionValue()} />
        <Column
          field='originalFilename'
          width={300}
          minResizableWidth={300}
          cell={(gridCellProps: GridCellProps) => {
            if (
              !hasPermission(permissions, PermissionsEnum.CanLinkDocument) ||
              isDocumentSanitized(gridCellProps.dataItem.id)
            ) {
              return <td>{gridCellProps.dataItem.originalFilename}</td>
            }

            return (
              <td style={{ color: 'var(--link)', textDecoration: 'underline', cursor: 'pointer' }}>
                <span
                  onClick={() =>
                    showDocumentPreviewModal({
                      name: gridCellProps.dataItem.name,
                      id: gridCellProps.dataItem.id,
                    })
                  }
                >
                  {gridCellProps.dataItem.originalFilename}
                </span>
              </td>
            )
          }}
          title='FileName'
        />
        <Column width={150} minResizableWidth={150} field='description' title='Description' />
        <Column width={120} minResizableWidth={120} field='category.name' title='Category' />
        <Column width={120} minResizableWidth={120} field='type.name' title='Type' />
        <Column
          width={120}
          minResizableWidth={120}
          cell={(gridCellProps: GridCellProps) => {
            if (!hasPermission(permissions, PermissionsEnum.CanLinkDocument)) {
              return <td>{gridCellProps.dataItem.status.name}</td>
            }
            return (
              <td
                className='table-columns__link'
                onClick={() => showHistoryDetails(gridCellProps.dataItem.id)}
              >
                {gridCellProps.dataItem.status.name}
              </td>
            )
          }}
          title='Status'
        />
        <Column
          width={120}
          minResizableWidth={120}
          cell={(gridCellProps: GridCellProps) => {
            const statusHistory: DocumentHistory[] = gridCellProps.dataItem.statusHistory || []
            if (!statusHistory.length) {
              return <td />
            }

            const sortedData = [...statusHistory].sort((a, b) =>
              dateComparator(a.createdDate, b.createdDate, true)
            )
            if (!isDocumentStatusRejected(sortedData[0].status.id)) {
              return <td />
            }
            return <td>{sortedData[0].reason}</td>
          }}
          title='Rejection Reason'
        />
        <Column
          field='createdAt'
          width={150}
          minResizableWidth={150}
          title='Date Created'
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.createdAt, true)}</td>
          )}
        />
        <Column
          title='Tags'
          width={160}
          minResizableWidth={160}
          field='tags'
          cell={(gridCellProps: GridCellProps) => (
            <td>
              {gridCellProps.dataItem.tags.map((tagType: TagType) => (
                <RoleTagId
                  userRoleName={tagType.name}
                  role={{ name: tagType.name }}
                  key={tagType.id}
                />
              ))}
            </td>
          )}
        />
        <Column
          title='DocV'
          width={150}
          minResizableWidth={150}
          field='verificationStatus.status.name'
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem.verificationStatus) {
              return <td />
            }
            return (
              <td
                className={generateClassNameForCell(
                  gridCellProps.dataItem.verificationStatus.status
                )}
                onClick={() =>
                  setReportModal({
                    reportId: gridCellProps.dataItem.verificationStatus.reportId,
                    document: gridCellProps.dataItem,
                  })
                }
              >
                {gridCellProps.dataItem.verificationStatus.status.name}
              </td>
            )
          }}
        />
        <Column width={100} minResizableWidth={100} title='Number ID' field='data.number' />
        <Column
          width={100}
          minResizableWidth={100}
          title='Personal Number'
          field='data.personalIdNumber'
        />
        <Column
          field='expirationAt'
          title='Expiration'
          width={150}
          minResizableWidth={150}
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.expirationAt)}</td>
          )}
        />
        <Column
          width={150}
          minResizableWidth={150}
          field='data.country.name'
          title='Country'
          className='table-columns__content'
        />
        <Column
          title='Notified'
          width={120}
          minResizableWidth={120}
          cell={(props: GridCellProps) => (
            <td>
              <input
                type='checkbox'
                checked={props.dataItem.documentRejectedClientNotified}
                readOnly
                disabled={true}
              />
            </td>
          )}
        />
        <Column
          title=''
          width={50}
          locked
          minResizableWidth={50}
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__sticky'>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button
                      type='button'
                      className={styles.table_menu_button}
                      aria-label='More options'
                    />
                  }
                >
                  <MoreIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {renderMenu(gridCellProps.dataItem)}
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          )}
        />
      </Grid>
    </Fragment>
  )
}

export default ClientDetailsDocuments
